import * as angular from 'angular';
import { from as observableFrom, Observable, Subscription } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { NotebookSecretFacade, NotebookTypes } from '@mode/notebooks/contract';
import { AppStore } from '~/types/main.types';

import { MixableBaseController } from '../../../common/component';
import { dispatchEvent } from '@mode/shared/util-js';
import { SessionResource } from '@mode/legacy/data-access-model-js';
import { NormalizedNotebookCell } from '@mode/legacy/types-app-state';
import { AceLanguage, NotebookSelectors } from '../../../selectors/notebook.selectors';
import { NotebookDataService } from '../../../services/notebook-data-service';

class NotebookCellEditorController extends MixableBaseController {
  static $inject = ['$timeout', '$window', 'RxStoreService', 'NotebookDataService', 'NotebookSecretService'];

  cell: NormalizedNotebookCell;
  notebookToken: string;
  session?: SessionResource;
  aceControls: any;
  readOnly: boolean;
  showInLayout: boolean;

  vizChangeSubscriber: Subscription;
  editingSubscriber: Subscription;

  cellContent: string;
  markdownText: string;
  executionCount$: Observable<string>;
  isEditing$: Observable<boolean>;
  secretKeys$: Observable<string[]>;
  isMarkdownViewMode$: Observable<boolean>;
  lang$: Observable<AceLanguage>;
  typeAbbr$: Observable<'Md' | 'Py' | 'R'>;

  constructor(
    private $timeout: angular.ITimeoutService,
    private $window: angular.IWindowService,
    private rxStore: AppStore,
    private notebookDataService: NotebookDataService,
    private notebookSecretFacade: NotebookSecretFacade
  ) {
    super();

    this.aceControls = {};
  }

  $onInit() {
    this.isEditing$ = this.rxStore.select(NotebookSelectors.createIsCellEditingSelector(this.cell.token));
    this.secretKeys$ = this.notebookSecretFacade.secretKeys$;
    this.executionCount$ = (
      this.cell
        ? this.rxStore.select(NotebookSelectors.createCellExecutionCountSelector(this.cell.token))
        : observableFrom([0])
    ).pipe(map((count) => `[${count || '_'}]`));
    this.isMarkdownViewMode$ = this.rxStore.select(NotebookSelectors.createMarkdownViewModeSelector(this.cell.token));

    // cell shouldn't be empty, as it should be required to render this component
    if (this.cell) {
      this.cellContent = this.cell.content;
      this.lang$ = this.rxStore.select(NotebookSelectors.createCellAceLangSelector(this.cell.token));
      this.typeAbbr$ = this.lang$.pipe(
        map((t) => {
          switch (t) {
            case AceLanguage.Markdown:
              return 'Md';
            case AceLanguage.Python:
              return 'Py';
            case AceLanguage.R:
              return 'R';
          }
        })
      );
    }

    this.isEditing$.pipe(takeUntil(this.destroyed$)).subscribe((editing) => {
      if (editing) {
        // Wait after current digest complete so the ace editor is visible before firing updates
        this.$timeout(() => {
          this.aceControls.focus && this.aceControls.focus();
          dispatchEvent(this.$window, 'resize');
        });
      }
    });
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.cell) {
      this.cellContent = this.cell.content;
      this.markdownText = (this.cellContent && this.cellContent.trim()) || 'Markdown text here...';
    }
  }

  handleTextChanged(text) {
    this.notebookDataService.editCellContent(this.cell.token, text);
  }

  toggleContentType() {
    this.notebookDataService.updateContentType(
      this.cell.token,
      this.cell.content_type === NotebookTypes.ContentType.Python
        ? NotebookTypes.ContentType.Markdown
        : NotebookTypes.ContentType.Python
    );
  }

  editMarkdown = () => {
    if (!this.cell.frozen && !this.readOnly) {
      this.notebookDataService.beginEditCell(this.cell.token);
    }
  };

  handleEditorClick = (event) => {
    const $target = $(event.target);

    if (!$target.closest('mode-notebook-cell-ace').length && this.cell.content_type === 'python') {
      // did not click in ace editor so focus on editor
      this.editMarkdown();
    }
  };
}

export const notebookCellEditorComponent: angular.IComponentOptions = {
  bindings: {
    cell: '<',
    notebookToken: '<',
    session: '<?',
    readOnly: '<',
    showInLayout: '<?',
  },
  controller: NotebookCellEditorController,
  controllerAs: 'ctrl',
  template: `
    <div class="mode-notebook-cell" ng-class="{ 'with-markdown': (ctrl.isMarkdownViewMode$ | async:this), 'editing': (ctrl.isEditing$ | async:this), 'mode-notebook-cell-folded': ctrl.cell.folded }">
      <div class="mode-notebook-cell-header" ng-if="ctrl.cell.content_type === 'python'">
        <capra-icon icon="icon-refresh" ng-if="ctrl.cell.executing"></capra-icon>
        <div ng-if="!ctrl.cell.executing && ctrl.cell.frozen" class="mode-notebook-icon-status align-md-icon icon-freeze-gray" uib-tooltip="This cell is frozen and will not run unless it is unfrozen" tooltip-class="wrapping-tooltip" tooltip-placement="bottom" tooltip-enable="ctrl.cell.frozen" tooltip-append-to-body="true"></div>
        IN {{ ctrl.executionCount$ | async:this }}

        <mode-state-indicator-icon
          ng-if="ctrl.showInLayout"
          display-state-icon="true"
          main-icon="icon-cell"
          tooltip-placement="bottom"
          state-icon="icon-check"
          tooltip-html-content="This cell is included in the report"
          class="notebook-cell-state-indicator --input-cell"
          data-test="notebook-cell-state-indicator"
        ></mode-state-indicator-icon>
      </div>
      <div class="mode-notebook-cell-label">{{ ctrl.typeAbbr$ | async:this }}</div>
      <div class="mode-notebook-editor-cell" ng-class="{'mode-notebook-cell-frozen':ctrl.cell.frozen}">
        <!-- Notebook Viewer -->
        <mode-live-code ng-if="ctrl.readOnly && ctrl.cell.content_type === 'python'" lang="{{ ctrl.lang$ | async:this }}" line-numbers="true" code="ctrl.cell.content"></mode-live-code>
        <!-- Notebook Editor -->
        <mode-notebook-cell-ace ng-if="!ctrl.readOnly && !(ctrl.isMarkdownViewMode$ | async:this)" lang="ctrl.lang$ | async:this" ng-model="ctrl.cellContent" session="ctrl.session" control-events="ctrl.aceControls" read-only="ctrl.cell.frozen" on-text-changed="ctrl.handleTextChanged($text)" secret-keys="ctrl.secretKeys$ | async:this"></mode-notebook-cell-ace>
        <!-- When this component gets migrated to angular this should get converted to <div [innerHtml]={{ markdownText | markdownParser }}> and mode-notebook-cell-markdown can be deleted -->
        <mode-notebook-cell-markdown ng-if="ctrl.isMarkdownViewMode$ | async:this" ng-dblclick="!ctrl.readOnly && ctrl.editMarkdown()" [markdown]="ctrl.markdownText"></mode-notebook-cell-markdown>
      </div>
    </div>
  `,
};
